import { computed, ref } from "vue";
import {
  type RouteLocationNormalizedGeneric,
  type RouteRecordName,
} from "vue-router";
import { storeToRefs } from "pinia";
import { AppIdentifier, RoleActions } from "@/shared/@types";
import { useAccountStore } from "@/shared/stores";
import { usePermission } from "@/shared/composables";
import { AppRouteNames } from "@/app/shared/@types";

export const useAuthenticatedUsersOnly = (
  to: RouteLocationNormalizedGeneric,
) => {
  const accountStore = useAccountStore();
  const { user, fetchingUserError, sapCustomerExists } =
    storeToRefs(accountStore);

  const { hasPermission } = usePermission();

  // ----- Case: User data is not available ---------------------------------------------------
  const hasUserData = computed(() => !!user.value);
  const hasFetchingUserError = computed(() => !!fetchingUserError.value);

  // If no user is available fetch it and set a initial customer directly
  const initializeUserData = async (appToVisit: AppIdentifier) => {
    await accountStore.fetchUser();

    if (!hasFetchingUserError.value) {
      const initialCustomerId =
        accountStore.getInitialSelectedCustomerId(appToVisit);

      accountStore.setSelectedCustomerId(initialCustomerId);
    }
  };

  // ----- Case: User has not completed registration for Fleet at Home ------------------------
  // User needs to complete registration if address or bank details are missing and the user has access the Fleet at Home app
  const routesAccessibleWithoutCustomer: RouteRecordName[] = [
    AppRouteNames.FinishRegistration,
    AppRouteNames.ThgQuoteNotFound,
    AppRouteNames.ThgQuoteOverview,
    AppRouteNames.ThgQuoteUnauthenticatedVehicleRegistration,
    AppRouteNames.ThgQuoteVehicleRegistration,
  ];

  const accessRouteWithCustomerRequired = ref(
    !routesAccessibleWithoutCustomer.includes(to.name),
  );

  const needsToCompleteRegistration = computed(
    () =>
      !sapCustomerExists.value &&
      hasPermission(RoleActions.ACCESS_FLEET_AT_HOME_APP),
  );

  // ----- Case: User comes from wid-services and has no access to the app ---------------------
  // Then we try to set a customer id that has permission
  const hasCustomerWithPermission = (appToVisit: AppIdentifier) => {
    const desiredCustomerId =
      accountStore.getInitialSelectedCustomerId(appToVisit);

    if (!desiredCustomerId) {
      return false;
    } else {
      accountStore.setSelectedCustomerId(desiredCustomerId);
      return true;
    }
  };

  return {
    initializeUserData,
    hasCustomerWithPermission,
    hasUserData,
    hasFetchingUserError,
    accessRouteWithCustomerRequired,
    needsToCompleteRegistration,
  };
};
